import { m } from 'framer-motion';
import { Check, FileText } from 'lucide-react';
import { Spinner } from '@/components/ui/Feedback';

/**
 * Мелкие части диалога, связанные с вложениями: индикатор записи, проигрыватель
 * своей записи и карточка отправленного файла.
 */

/**
 * Индикатор идущей записи.
 *
 * Без него нажатие выглядит как бездействие: человек не понимает, слышит ли
 * его микрофон. Полосы двигаются от реального уровня сигнала, поэтому тишина
 * видна сразу.
 */
export function RecordingBar({ level, elapsedMs }: { level: number; elapsedMs: number }) {
  const bars = [0.35, 0.7, 1, 0.8, 0.5, 0.9, 0.6];

  return (
    <div
      role="status"
      className="mb-2 flex items-center gap-3 rounded-panel border border-line/70 bg-surface px-3 py-2.5"
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand" />
      </span>

      <div className="flex h-6 flex-1 items-center gap-[3px]" aria-hidden>
        {bars.map((weight, index) => (
          <m.span
            key={index}
            className="w-[3px] rounded-full bg-brand"
            animate={{ height: `${Math.max(4, Math.min(24, level * weight * 40 + 4))}px` }}
            transition={{ duration: 0.12 }}
          />
        ))}
      </div>

      <span className="shrink-0 text-[12px] tabular-nums text-muted">{formatDuration(elapsedMs)}</span>
    </div>
  );
}

/**
 * Прослушивание своей записи.
 *
 * Сервер хранит расшифровку, но не звук, поэтому запись доступна только на
 * этом устройстве и только пока открыт диалог.
 */
export function VoicePlayback({
  url,
  durationMs,
  pending,
}: {
  url: string;
  durationMs?: number;
  /** Запись ещё отправляется и расшифровывается. */
  pending?: boolean;
}) {
  return (
    <div className="mt-2">
      <audio src={url} controls className="h-8 w-full" preload="metadata" />
      <span className="mt-1 flex items-center gap-1.5 text-[11px] opacity-80">
        {pending ? (
          <>
            {/*
              Расшифровка занимает секунды, а на длинной записи — заметно
              больше. Без отметки кажется, что запись ушла в никуда
            */}
            <Spinner size={11} />
            Расшифровываю запись…
          </>
        ) : durationMs ? (
          formatDuration(durationMs)
        ) : null}
      </span>
    </div>
  );
}

/** Отправленный файл в своём сообщении: без него отправка выглядит как ничего. */
export function SentFile({ name, pending }: { name: string; pending?: boolean }) {
  return (
    <div className="mt-2 flex items-center gap-2 rounded-control border border-white/25 bg-white/10 px-2.5 py-2 text-[12px]">
      <FileText className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
      <span className="truncate">{name}</span>
      <span className="ml-auto shrink-0 opacity-80" aria-hidden>
        {pending ? <Spinner size={12} /> : <Check className="h-3.5 w-3.5" />}
      </span>
    </div>
  );
}

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
